import React, { useEffect } from "react";
import Helmet from "react-helmet";
import { helmetJsonLdProp } from "react-schemaorg";

import { Container, Col, Row } from "react-bootstrap/esm/index";

// Import Components
import ITPHeroHeader from "../../components/HeroHeader/ITPHeroHeader";
import PrimaryButton from "../../components/shared-ui/Buttons/PrimaryButton";
import ItpNavigation from "../../components/ITPNavigation/ITPNavigation";
import FooterITP from "../../components/shared-ui/Footer/Footer-ITP";
import BackToTopBtn from "../../components/shared-ui/BackToTopBtn/BackToTopBtn";
import ISIITPinline from "../../components/shared-ui/ISI/ISIITP-inline";
import ISIwrapper from "../../components/shared-ui/ISI/ISI-wrapper";
import ISIITP from "../../components/shared-ui/ISI/ISIITP";
import CardFlipper from "../../components/shared-ui/Cards/CardFlipper";
import LookingForSupport from "../../components/shared-ui/Cards/LookingForSupport";

function SettingGoals() {
  useEffect(() => {
    window.scrollTo(0, 0);
    let location = window.location.pathname;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      page: {
        path: `${location}`,
        title: "Immune Thrombocytopenia (ITP) | DOPTELET® (avatrombopag)",
        environment: "dev",
        indication: "ITP",
        audience: "DTC",
      },
    });
  }, []);
  return (
    <div className="main_content_wrap">
      <Helmet
        script={[
          helmetJsonLdProp({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "MedicalWebPage",
                url: "https://doptelet.com/setting-goals/",
                name: "Chronic Immune Thrombocytopenia (ITP) Treatment | DOPTELET® (avatrombopag)",
                mainEntity: {
                  "@type": "MedicalCondition",
                  name: "chronic immune thrombocytopenia",
                  alternateName: "ITP",
                },
                mainContentOfPage: [
                  "SET AND REACH YOUR CHRONIC ITP TREATMENT GOALS",
                  "Living with low platelet counts can be hard, but talking about it doesn't have to be. Having an open and honest conversation with your doctor can help lead to care that best fits all your treatment needs.",
                ],
                lastReviewed: "2025-09-01",
                reviewedBy: {
                  "@type": "Organization",
                  name: "Sobi",
                  url: "https://www.sobi.com/usa/",
                },
              },
            ],
          }),
        ]}
      >
        <title>
          Chronic Immune Thrombocytopenia (ITP) Treatment | DOPTELET®
          (avatrombopag)
        </title>
        <meta
          name="description"
          content="Discover DOPTELET® (avatrombopag), a medication used to increase low blood platelet counts in adults with chronic immune thrombocytopenia (ITP)."
        />
        <link rel="canonical" href="https://doptelet.com/setting-goals/" />
      </Helmet>
      <ItpNavigation />
      <div className="inner-content-wrap itpHome">
        <ITPHeroHeader
          extraClass="large-hero inner-bg"
          markup={
            <>
              <h1>
                SET AND REACH YOUR CHRONIC ITP TREATMENT GOALS
              </h1>
              <p className="mb-0">
                Living with low platelet counts can be hard, but talking about it doesn’t have to be. Having an open and honest conversation with your doctor can help lead to care that best fits all your treatment needs.
              </p>
            </>
          }
          maxWidth="500"
          bgImage={["managing-itp-hero.jpg", "managing-itp-hero.jpg"]}
          bgImageMobile={[
            "managing-itp-hero-mobile.jpg",
            "managing-itp-hero-mobile.jpg",
          ]}
          imageAlt="Doptelet platelet characters on amusement park swing ride"
        />

        <section className="section-padding">
          <Container>
            <Row>
              <Col lg={{ span: 12 }}>
                <h2>
                  Treatment goal discussion
                </h2>
                <p>Goals with chronic ITP are not one-size-fits-all. That’s why partnering with your doctor and sharing in the decision-making process is a great way to feel more in control of your treatment.</p>
                <p><strong>Here are a few questions to consider before starting a conversation with your doctor:</strong></p>
              </Col>
            </Row>
            <Row>
              <Col lg={4} md={12}>
                <CardFlipper
                  front={
                    <>
                      <img src="/images/platey_jumping.png" alt="Platey jumping" className="mb-3" />
                      <h3>Health Goals</h3>
                      <p className="mb-0">Everyone has their own likes and dislikes, which can affect the treatment you and your doctor choose together.</p>
                    </>
                  }
                  back={
                    <>
                      <h3 className="text-white">Health Goals</h3>
                      <ul>
                        <li>What is your most important treatment goal and why?</li>
                        <li>How do you feel about your current treatment and the day-to-day impact on your life?</li>
                        <li>How long will it take for these treatments to start working?</li>
                        <li>What side effects might these treatments have, and will they affect any immunizations you may need to get?</li>
                      </ul>
                    </>
                  }
                />
              </Col>
              <Col lg={4} md={12}>
                <CardFlipper
                  front={
                    <>
                      <img src="/images/platey_hammer.png" alt="Platey swinging hammer" className="mb-3" />
                      <h3>Treatment Choices</h3>
                      <p className="mb-0">Certain chronic ITP treatments require injections for dosing or food-type restrictions (like restrictions on dairy or iron supplements) that may change your routine.</p>
                    </>
                  }
                  back={
                    <>
                      <h3 className="text-white">Treatment Choices</h3>
                      <ul>
                        <li>Are you satisfied with your current treatment?</li>
                        <li>Would you rather take a pill or get an injection for dosing?</li>
                        <li>Would you be willing to plan your meals around your treatment?</li>
                        <li>Will these treatments interact with any other medicines that you take or foods that you eat?</li>
                      </ul>
                    </>
                  }
                />
              </Col>
              <Col lg={4} md={12}>
                <CardFlipper
                  front={
                    <>
                      <img src="/images/platey_baseball.png" alt="Platey throwing a baseball" className="mb-3" />
                      <h3>Other Goals</h3>
                      <p className="mb-0">It’s important to consider all of your options when choosing a treatment, including how it fits into your work schedule, ease of travel, and if you can find financial assistance or patient support programs.</p>
                    </>
                  }
                  back={
                    <>
                      <h3 className="text-white">Other Goals</h3>
                      <ul>
                        <li>Have you missed or had to skip any treatment doses in the last few months? If so, why?</li>
                        <li>Have you missed work to go your doctor’s appointments?</li>
                        <li>Are you able to get to your doctor’s appointments easily? (think: car, bus, train, etc)</li>
                        <li>When, where, and for how long will you need to take these treatments?</li>
                        <li>Are you interested in financial assistance or patient support programs?</li>
                      </ul>
                    </>
                  }
                />
              </Col>
            </Row>
          </Container>
        </section>

        <section className="cloud-blue-bg section-padding">
          <Container>
            <Row className="align-items-center">
              <Col lg={{ span: 12 }}>
                <h2 className="mb-4">Treatment takes teamwork</h2>
                <div className="d-flex align-items-center flex-row mb-4">
                  <img
                    style={{maxWidth: 64}}
                    src="/images/tips-icon.png"
                    alt="Doctor Discussion Guide"
                    className="mr-3 mb-4 mb-md-0"
                  />
                  <h3 className="text-blue mb-0">Doctor Discussion Guide</h3>
                </div>
                <p>Working with your doctor can help raise your chances of finding a treatment that fits your needs and goals. Be sure to take advantage of the Doctor Discussion Guide. You can download it and print it out for support when having those important conversations with your doctor.</p>
                <PrimaryButton
                  title="DOWNLOAD GUIDE"
                  href="/pdf/Dr-Discussion-Guide.pdf"
                  addClass="btn-primary download download-white"
                  target="_blank"
                />
              </Col>
            </Row>
          </Container>
        </section>

        <LookingForSupport addClass="section-padding" />

        <ISIITPinline />
      </div>
      <FooterITP />
      <ISIwrapper>
        <ISIITP />
      </ISIwrapper>
      <BackToTopBtn />
    </div>
  );
}

export default SettingGoals;
